import React, {Component} from 'react';
import PropTypes from 'prop-types';
import {FormattedMessage} from 'react-intl';

import {ENTITY_TYPE_VENUE, ENTITY_TYPE_CITY} from '@stubhub/entity-helper';
import {keys} from '@stubhub/react-date-filter';
import FeatureToggle from '@stubhub/react-feature-toggle';
import {ssr_wait, connect} from '@stubhub/react-store-provider';
import {theme} from '@stubhub/react-theme-provider';

import EntityList, {RenderLoadingState} from '../../modules/react-entity-list';
import {CATEGORY_IDS} from '../../utils/constants';
import CBTModule from '../react-cbt-module';
import VenueList from '../react-city-venue-list';
import SeoCityVenues from '../react-seo-city-venues';
import ConnectedNearbyCities from '../react-seo-nearby-cities';
import SubGenreDropDown from '../react-subgenre-dropdown';

import controller from './controller';
import EmptyState from './emptyState.js';

import './index.scss';

@theme
class CityPage extends Component {
  static contextTypes = {
    queue: PropTypes.object,
    globalRegistry: PropTypes.object,
    dateKey: PropTypes.oneOf([keys.K_CHOOSE, keys.K_THIS_MONTH, keys.K_THIS_WKD, keys.K_TODAY]),
  };

  constructor(props, context) {
    super(props, context);
    const {globalRegistry = {}} = context /* istanbul ignore next */ || {};
    const {getFeatureValue} = globalRegistry;

    if (typeof getFeatureValue === 'function') {
      this.isDateFilterEnabled = globalRegistry.getFeatureValue('geography.citypage.enableDateFilter');
      this.isNearbyCitiesEnabled = globalRegistry.getFeatureValue('geography.citypage.enableNearbyCities');
    }
    // Store each sub genre id to local state
    this.state = {
      sports: {
        name: <FormattedMessage id="Citypage.State.Sports" defaultMessage="Sports" />,
        type: 'category',
        ids: [CATEGORY_IDS.SPORTS],
        key: 0,
      },
      concerts: {
        name: <FormattedMessage id="Citypage.State.Concerts" defaultMessage="Concerts" />,
        type: 'category',
        ids: [CATEGORY_IDS.CONCERTS],
        key: 0,
      },
      theater: {
        name: <FormattedMessage id="Citypage.State.Theater" defaultMessage="Theater" />,
        type: 'category',
        ids: [CATEGORY_IDS.THEATER],
        key: 0,
      },
      whatChanged: 'sports', // Using this state to host last changed sub genre to prevent duplicate empty state tracking,
    };
  }

  @ssr_wait
  init() {
    /*
     * When page load, set reset all three categories
     * return this.loadEvents(this.state, true, true, true);
     */
    return this.loadEvents(this.state);
  }

  UNSAFE_componentWillMount() {
    const {sportEvents, theaterEvents, concertEvents, updateTheme} = this.props;
    /* istanbul ignore else */
    if (!sportEvents.data || !theaterEvents.data || !concertEvents.data) {
      this.init();
    }
    // Update citypage theme
    if (typeof updateTheme === 'function') {
      const theme = {
        unifiedLayout: {
          paletteSecondary: '#FFFFFF',
        },
      };
      updateTheme(theme);
    }
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    const {eventStartDate, eventEndDate} = this.props;
    if (nextProps.eventStartDate !== eventStartDate || nextProps.eventEndDate !== eventEndDate) {
      // When date filter value changed, reset all three categories
      this.loadEvents(nextProps, true, true, true);
    }
  }

  shouldComponentUpdate(nextProps) {
    const {dateFilter} = this.props;
    // If date filter is changed, but data request has not been sent yet, DO NOT rerender.
    const currentDateFilter = dateFilter || {};
    const nextDateFilter = nextProps.dateFilter || {};

    return (
      currentDateFilter.key === nextDateFilter.key &&
      currentDateFilter.start === nextDateFilter.start &&
      currentDateFilter.end === nextDateFilter.end
    );
  }

  componentWillUnmount() {
    const {updateTheme, resetCityPage} = this.props;
    // Clear theme before component unmount
    if (typeof updateTheme === 'function') {
      const theme = {};
      updateTheme(theme);
    }
    resetCityPage && resetCityPage();
  }

  loadEvents(props, resetSports, resetConcerts, resetTheater) {
    // Pass sub genre info in state to loadEvents
    const newProp = {...this.props, ...this.state};
    const {loadEvents} = this.props;

    return loadEvents(newProp, {
      toggles: {isDateFilterEnabled: this.isDateFilterEnabled},
      resetSports,
      resetConcerts,
      resetTheater,
    });
  }

  /**
   * Lazy load data fetching functions. Used by react-entity-list component.
   */
  fetchEvents = (props = {}) => {
    const {sportEvents, theaterEvents, loadEvents} = this.props;

    if (props.sports && sportEvents.offset === sportEvents.totalCount && !props.resetSports) {
      return;
    }

    if (props.theater && theaterEvents.offset === theaterEvents.totalCount && !props.resetTheater) {
      return;
    }

    const newSearchProps = {...this.props, ...props};

    return loadEvents(newSearchProps, {
      toggles: {isDateFilterEnabled: this.isDateFilterEnabled},
      resetSports: props.resetSports,
      resetConcerts: props.resetConcerts,
      resetTheater: props.resetTheater,
    });
  };

  /**
   * Lazy load sport events handler.
   */
  fetchSportEvents = () => {
    const {sports} = this.state;
    // Fetch sports events with correct sub genre id
    const newProp = {
      sports,
      resetSports: false,
      resetConcerts: false,
      resetTheater: false,
      loadMoreSports: true,
    };

    return this.fetchEvents(newProp);
  };

  /**
   * Lazy load theater events handler.
   */
  fetchTheaterEvents = () => {
    const {theater} = this.state;
    // Fetch theater events with correct sub genre id
    const newProp = {
      theater,
      resetSports: false,
      resetConcerts: false,
      resetTheater: false,
      loadMoreTheater: true,
    };

    return this.fetchEvents(newProp);
  };

  onSubGenreSelected = (item, genreModuleCategory) => {
    // Add selected category to store and update whatChanged
    this.setState({[genreModuleCategory]: item, whatChanged: genreModuleCategory});
    let resetSports = false;
    let resetConcerts = false;
    let resetTheater = false;
    if (genreModuleCategory === 'sports') {
      resetSports = true;
    } else if (genreModuleCategory === 'concerts') {
      resetConcerts = true;
    } else if (genreModuleCategory === 'theater') {
      resetTheater = true;
    }
    const fetchOptions = {[genreModuleCategory]: {...item}, resetSports, resetConcerts, resetTheater};

    return this.fetchEvents(fetchOptions);
  };

  renderNoResultsDesktop(buff) {
    const messageTitle = (
      <FormattedMessage id="Citypage.SubGenreModule.NothingFound.Title" defaultMessage="We didn't find anything" />
    );
    const messageSubTitle = (
      <FormattedMessage
        id="Citypage.SubGenreModule.NothingFound.SubTitle"
        defaultMessage="Try another category or different date."
      />
    );

    return (
      <div className="citypage-genre-module--no-results">
        <div className="genre-module--no-results-desktop">
          <div className="genre-module--no-results--message">
            <div className="genre-module--no-results--message--title">{messageTitle}</div>
            <div className="genre-module--no-results--message--subtitle">{messageSubTitle}</div>
          </div>
          {[...Array(buff)].map((e, i) => (
            <RenderLoadingState key={i} />
          ))}
        </div>
      </div>
    );
  }

  isDesktop = (deviceType) => {
    return deviceType === 'desktop';
  };

  render() {
    const {
      lat,
      lng,
      isLoading,
      isConcertLoading,
      screenSize,
      entityId,
      entityName,
      entityWebURI,
      sportEvents,
      theaterEvents,
      concertEvents,
      enableSubGenreModuleMweb,
      enableSubGenreModuleDesktop,
      deviceType,
      isSportLoading,
      isTheaterLoading,
      theaterLoadMore,
      sportsLoadMore,
      concertsLoadMore,
      isCBTModuleEnabled,
      isRedirectModalEnabled,
    } = this.props;
    const venueListTitle =
      !enableSubGenreModuleMweb && !enableSubGenreModuleDesktop ? (
        <FormattedMessage
          id="Citypage.GenreModule.Concerts.Title"
          defaultMessage="Concerts in {cityName}"
          values={{cityName: entityName}}
        />
      ) : (
        ''
      );
    const sportsTitle =
      !enableSubGenreModuleMweb && !enableSubGenreModuleDesktop ? (
        <FormattedMessage
          id="Citypage.GenreModule.Sports.Title"
          defaultMessage="Sports in {cityName}"
          values={{cityName: entityName}}
        />
      ) : (
        ''
      );
    const theaterComedyTitle =
      !enableSubGenreModuleMweb && !enableSubGenreModuleDesktop ? (
        <FormattedMessage
          id="Citypage.GenreModule.TheaterComedy.Title"
          defaultMessage="Theater & Comedy in {cityName}"
          values={{cityName: entityName}}
        />
      ) : (
        ''
      );

    const {sports, concerts, theater} = this.state;
    const shouldRenderEmptyState = sports.key === 0 && theater.key === 0 && concerts.key === 0;
    const shouldRenderSubGenreEmptyForSport = sports.key !== 0;
    const shouldRenderSubGenreEmptyForTheater = theater.key !== 0;
    const shouldRenderSubGenreEmptyForConcert = concerts.key !== 0;
    const commonParams = {
      type: 'event',
      point: `${lat},${lng}`,
    };
    let cityVenues = [];
    let venueLinks = [];
    let emptyState = false;
    const hasConcertEvents = concertEvents.data && concertEvents.data.length > 0;
    const hasSportEvents = sportEvents.data && sportEvents.data.length > 0;
    const hasTheaterEvents = theaterEvents.data && theaterEvents.data.length > 0;
    // Logic to add four black elements to entities group
    let itemsSports = sportEvents.data || [];
    let itemsTheater = theaterEvents.data || [];
    let itemsConcerts = concertEvents.data || [];
    let customeClass = '';
    // Set placer holder number based on screensize
    const buff = screenSize === 'small' ? 2 : screenSize === 'medium' ? 3 : 4;
    // If sub genre module feature turned on, add placeholder cards while loading each genre
    if (enableSubGenreModuleDesktop || enableSubGenreModuleMweb) {
      customeClass = 'citypage-subgenre-event-card';
      if (isSportLoading) {
        if (!sportsLoadMore) {
          itemsSports = [];
        }
        for (let i = 0; i < buff; i++) {
          itemsSports = itemsSports.concat([null]);
        }
      }
      if (isTheaterLoading) {
        const limit = theaterEvents.totalCount || buff;
        if (!theaterLoadMore) {
          itemsTheater = [];
        }
        for (let i = 0; i < buff && itemsTheater.length < limit; i++) {
          itemsTheater = itemsTheater.concat([null]);
        }
      }
      if (isConcertLoading) {
        const limit = concertEvents.totalCount || buff;
        if (!concertsLoadMore) {
          itemsConcerts = [];
        }
        for (let i = 0; i < buff && itemsConcerts.length < limit; i++) {
          itemsConcerts = itemsConcerts.concat([null]);
        }
      }
    }

    /* istanbul ignore else */
    if (!isConcertLoading && hasConcertEvents) {
      cityVenues = concertEvents.data.slice(0, MAX_VENUE_CARD_COUNT);
      venueLinks = concertEvents.data
        .slice(MAX_VENUE_CARD_COUNT, MAX_VENUE_CARD_COUNT + MAX_VENUE_SEO_LINKS_COUNT)
        .map((item) => item.venue);
    }

    const mWebCarouselProps = {};
    commonParams.useCarousel = this.isDesktop(deviceType);

    if (
      this.isDateFilterEnabled &&
      !isLoading &&
      !hasConcertEvents &&
      !hasSportEvents &&
      !hasTheaterEvents &&
      shouldRenderEmptyState
    ) {
      // Variable used to display empty state
      emptyState = true;
    }

    return (
      <div className="mainContainer">
        <div className="CityPage__Body">
          {emptyState && (
            <>
              <FeatureToggle name="!homepage.showUKcmaFeatures">
                <EmptyState cityName={entityName} />
              </FeatureToggle>
              {isCBTModuleEnabled && <CBTModule entityType={ENTITY_TYPE_CITY} webUri={entityWebURI} />}
            </>
          )}
          {!emptyState && (
            <div className="VenueList__Wrapper Expand">
              {(enableSubGenreModuleMweb || enableSubGenreModuleDesktop) &&
                (hasSportEvents || shouldRenderSubGenreEmptyForSport || isSportLoading) && (
                  <SubGenreDropDown
                    enableSubGenreModuleMweb={enableSubGenreModuleMweb}
                    enableSubGenreModuleDesktop={enableSubGenreModuleDesktop}
                    genreModuleCategory="sports"
                    onSelection={this.onSubGenreSelected.bind(this)}
                    selected={sports}
                  />
                )}
              <EntityList
                promoteHeaderEl
                categoryId={28}
                screenSize={screenSize}
                resetPage={!sportsLoadMore}
                title={sportsTitle}
                entityData={itemsSports}
                hasMore={sportEvents.hasMore}
                offset={sportEvents.offset}
                totalCount={sportEvents.totalCount}
                fetchEntity={this.fetchSportEvents}
                {...mWebCarouselProps}
                {...commonParams}
                className={customeClass}
                isRedirectModalEnabled={isRedirectModalEnabled}
              />
              {!isSportLoading &&
                !hasSportEvents &&
                (enableSubGenreModuleMweb || enableSubGenreModuleDesktop) &&
                shouldRenderSubGenreEmptyForSport &&
                this.renderNoResultsDesktop(buff)}
              {(enableSubGenreModuleMweb || enableSubGenreModuleDesktop) &&
                (hasTheaterEvents || shouldRenderSubGenreEmptyForTheater || isTheaterLoading) && (
                  <SubGenreDropDown
                    enableSubGenreModuleMweb={enableSubGenreModuleMweb}
                    enableSubGenreModuleDesktop={enableSubGenreModuleDesktop}
                    genreModuleCategory="theater"
                    onSelection={this.onSubGenreSelected.bind(this)}
                    selected={theater}
                  />
                )}
              <EntityList
                promoteHeaderEl
                categoryId={174}
                screenSize={screenSize}
                resetPage={!theaterLoadMore}
                title={theaterComedyTitle}
                entityData={itemsTheater}
                hasMore={theaterEvents.hasMore}
                offset={theaterEvents.offset}
                totalCount={theaterEvents.totalCount}
                fetchEntity={this.fetchTheaterEvents}
                {...mWebCarouselProps}
                {...commonParams}
                className={customeClass}
                isRedirectModalEnabled={isRedirectModalEnabled}
              />
              {!isTheaterLoading &&
                !hasTheaterEvents &&
                (enableSubGenreModuleMweb || enableSubGenreModuleDesktop) &&
                shouldRenderSubGenreEmptyForTheater &&
                this.renderNoResultsDesktop(buff)}
              {(enableSubGenreModuleMweb || enableSubGenreModuleDesktop) &&
                (hasConcertEvents || shouldRenderSubGenreEmptyForConcert || isConcertLoading) && (
                  <SubGenreDropDown
                    enableSubGenreModuleMweb={enableSubGenreModuleMweb}
                    enableSubGenreModuleDesktop={enableSubGenreModuleDesktop}
                    genreModuleCategory="concerts"
                    onSelection={this.onSubGenreSelected.bind(this)}
                    selected={concerts}
                  />
                )}
              {!isConcertLoading && hasConcertEvents && (
                <VenueList
                  screenSize={screenSize}
                  title={venueListTitle}
                  venues={cityVenues}
                  resetPage={concertEvents.resetPage}
                  enableSubGenreModuleDesktop={enableSubGenreModuleDesktop}
                  isRedirectModalEnabled={isRedirectModalEnabled}
                />
              )}
              {isConcertLoading && (
                <EntityList
                  promoteHeaderEl
                  categoryId={174}
                  screenSize={screenSize}
                  resetPage={!concertsLoadMore}
                  title={theaterComedyTitle}
                  entityData={itemsConcerts}
                  hasMore={false}
                  totalCount={buff}
                  fetchEntity={this.fetchTheaterEvents}
                  {...mWebCarouselProps}
                  {...commonParams}
                  className={customeClass}
                  isRedirectModalEnabled={isRedirectModalEnabled}
                />
              )}
              {isCBTModuleEnabled && <CBTModule entityType={ENTITY_TYPE_CITY} webUri={entityWebURI} />}
              {!isLoading && venueLinks.length !== 0 && (
                <SeoCityVenues
                  entityType={ENTITY_TYPE_VENUE}
                  entityId={entityId}
                  entityName={entityName}
                  venueLinks={venueLinks}
                />
              )}
              {!isConcertLoading &&
                !hasConcertEvents &&
                (enableSubGenreModuleMweb || enableSubGenreModuleDesktop) &&
                shouldRenderSubGenreEmptyForConcert &&
                this.renderNoResultsDesktop(buff)}
              {this.isNearbyCitiesEnabled && (
                <ConnectedNearbyCities
                  entityType={ENTITY_TYPE_VENUE}
                  entityId={entityId}
                  entityName={entityName}
                  lat={lat}
                  lng={lng}
                />
              )}
            </div>
          )}
        </div>
      </div>
    );
  }
}

CityPage.propTypes = {
  /** Concert events object with totalCount, offset, data...*/
  concertEvents: PropTypes.object,

  /** Are there more concerts events? */
  concertsLoadMore: PropTypes.bool,

  /** Date filter object data */
  dateFilter: PropTypes.object,

  /** Device type */
  deviceType: PropTypes.string,

  /** Is sub genre module feature on for desktop? */
  enableSubGenreModuleDesktop: PropTypes.bool,

  /** Is sub genre module feature on for mWeb? */
  enableSubGenreModuleMweb: PropTypes.bool,

  /** The entity id */
  entityId: PropTypes.number.isRequired,

  /** Entity (city) name */
  entityName: PropTypes.string.isRequired,

  /** The entity type */
  entityType: PropTypes.string.isRequired,

  /** The entity webURI */
  entityWebURI: PropTypes.string,

  /** Event end date filter */
  eventEndDate: PropTypes.string,

  /** Event start date filter */
  eventStartDate: PropTypes.string,

  /** Is concerts info loading? */
  isConcertLoading: PropTypes.bool,

  /** Is info loading? */
  isLoading: PropTypes.bool,

  /** Is sports data loading? */
  isSportLoading: PropTypes.bool,

  /** Is theater data loading? */
  isTheaterLoading: PropTypes.bool,

  /** Latitude */
  lat: PropTypes.number,

  /** Longitude */
  lng: PropTypes.number,

  /** Function to call for loading events */
  loadEvents: PropTypes.func.isRequired,

  /** Function to call to reset city page info */
  resetCityPage: PropTypes.func,

  /** Screen size name */
  screenSize: PropTypes.oneOf(['small', 'medium', 'large']),

  /** Sports events object with totalCount, offset, data...*/
  sportEvents: PropTypes.object,

  /** Are there more sports events? */
  sportsLoadMore: PropTypes.bool,

  /** Theater events object with totalCount, offset, data...*/
  theaterEvents: PropTypes.object,

  /** Are there more theater events? */
  theaterLoadMore: PropTypes.bool,

  /** Current Global theme object */
  theme: PropTypes.object,

  /** Cxs object for applying the theme. */
  updateTheme: PropTypes.func,

  /** If CBT feature is active for showing cbt module */
  isCBTModuleEnabled: PropTypes.bool,

  /** Is redirection modal enabled? */
  isRedirectModalEnabled: PropTypes.bool,
};

export const MAX_VENUE_CARD_COUNT = 5;
export const MAX_VENUE_SEO_LINKS_COUNT = 25;

export {CityPage};

export default connect(controller)(CityPage);
